<template>
    <div>
        <div class="upload-list-header">
            <p class="form-label">
                {{ label }}
                <el-tooltip v-if="label" key="LR" placement="top" :content="tooltipText">
                    <font-awesome-icon icon="exclamation-circle" class="dynamic-color" />
                </el-tooltip>
            </p>
            <div class="select-controls" v-if="uploadType !== templateUploadImageType.product">
                <el-checkbox
                    :model-value="isAllSelected"
                    :indeterminate="isIndeterminate"
                    @change="(val: boolean) => onSelectAllImage(val)"
                > 
                    {{ $t(`components.mesh-loader.selectAll`) }}
                </el-checkbox>
                <el-button 
                    type="danger" 
                    size="small"
                    @click="handleDeleteAll"
                    class="delete-selected-button"
                > 
                    {{ $t(`components.mesh-loader.delete`) }} ({{ selectedCount }})
                </el-button>
            </div>
        </div>
        <el-upload
            v-model:file-list="localFileList"
            multiple
            class="image-placeholder"
            action="#"
            drag
            list-type="picture-card"
            :before-upload="handleBeforeUpload"
            :http-request="(res: any) => $emit('uploadFile', res, uploadType)"
            :accept="fileExtentions"
        >
            <template #default>
                <div class="upload-form">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <circle cx="8.5" cy="8.5" r="1.5" stroke="#999" stroke-width="2"/>
                        <path d="M21 15L16 10L5 21" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <div>
                        <p>{{ $t(`views.default.file.label`) }}</p>
                        <small class="text-muted">  {{ $t(`views.default.file.max`) }}: {{ maxSize }}MB</small>
                    </div>
                </div>
            </template>
            <template #file="{ file }">
            <div class="preview-wrapper">
                    <PreviewRendererImg
                    :meshes="[
                        {
                        thumbnailFiletype: $emit('getImageFileType', file.name),
                        thumbnail: file.name,
                        thumbnailUrl: file.url,
                        id: file.uid,
                        },
                    ]"
                    :can-select="false"
                    :can-remove="true"
                    @remove-item="handleDelete(file, uploadType)"
                    :imgHeight="150"
                    :selectedFileIds="selectedFileIds"
                    @handle-multi-select="(mesh: any, checked: boolean) => handleIndividualSelection(file, checked)"
                    />
                    <div class="filename">
                        <el-input v-model="file.name"/>
                    </div>
                </div>
            </template>
        </el-upload>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-facing-decorator';
import { ElUpload } from 'element-plus';
import PreviewRendererImg from '../previewRenderer/PreviewRendererImg.vue';
import { FileData } from '@/types/enum/file';
import { TemplateUploadImageType } from '@/types/enum/upload';

@Component({
  components: {
    ElUpload,
    PreviewRendererImg,
  },
  emits: [
    'beforeImageUpload',
    'uploadFile',
    'getImageFileType',
    'handleRemove',
    'update:fileList',
    'handleRemoveSelectedImage',
  ]
})
export default class ElUploadCustom extends Vue {
  @Prop({ required: true }) fileList!: FileData[]
  @Prop({ required: true }) uploadType!: TemplateUploadImageType
  @Prop({ default: '' }) label!: string
  @Prop({ default: '' }) tooltipText!: string
  @Prop({ default: 1 }) maxSize!: number
  @Prop({ default: '.jpg,.png,' }) fileExtentions!: string

  templateUploadImageType = TemplateUploadImageType;
  selectedFileIds: number[] = []

  get isAllSelected(): boolean {
    return this.fileList.length > 0 && this.selectedFileIds.length === this.fileList.length;
  }

  get isIndeterminate(): boolean {
    return this.selectedFileIds.length > 0 && this.selectedFileIds.length < this.fileList.length;
  }

  get selectedCount(): number {
    return this.selectedFileIds.length;
  }

  get localFileList(): FileData[] {
    return this.fileList
  }

  set localFileList(val: FileData[]) {
    this.$emit('update:fileList', val)
  }

  onSelectAllImage(checked: boolean): void {
    if (checked) {
      this.selectedFileIds = this.fileList.map(file => file.uid);
    } else {
      this.selectedFileIds = [];
    }
  }

  handleIndividualSelection(file: FileData, checked: boolean): void {
    if (checked) {
      if (!this.selectedFileIds.includes(file.uid)) {
        this.selectedFileIds.push(file.uid);
      }
    } else {
      this.selectedFileIds = this.selectedFileIds.filter(id => id !== file.uid);
    }
  }

  handleDelete(file: FileData, uploadType: TemplateUploadImageType): void{
    this.$emit('handleRemove', file, uploadType)
  }

  handleDeleteAll(): void {
    const selectedFiles = this.fileList.filter(file => this.selectedFileIds.includes(file.uid));
        
    if (selectedFiles.length === 0) return;

    this.$emit('handleRemoveSelectedImage', selectedFiles, this.uploadType);
  }

  getSelectedFiles(): FileData[] {
    return this.fileList.filter(file => this.selectedFileIds.includes(file.uid));
  }

  @Watch('fileList', { deep: true })
  onFileListChanged(newList: FileData[]): void {
    const existing = new Set(newList.map(f => f.uid));
    this.selectedFileIds = this.selectedFileIds.filter(id => existing.has(id));
  }

  async handleBeforeUpload(file: any): Promise<boolean> {
    return new Promise((resolve) => {
      this.$emit('beforeImageUpload', file, resolve, this.uploadType);
    });
  }
}
</script>

<style scoped>
.image-placeholder {
    background-color: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    display: flex;
    padding: 6px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;

    &:hover {
        background-color: #e9ecef;
        border-color: #adb5bd;
    }

    .placeholder-icon {
        opacity: 0.6;
        transition: opacity 0.15s ease-in-out;
    }

    &:hover .placeholder-icon {
        opacity: 0.8;
    }
}

.upload-form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;

    div {
        display: flex;
        flex-direction: column;
        gap: 0;
    }
    
    p  {
        font-size: 14px;
        margin: 0;
    }

    small {
        font-size: 10px;
    }
}

.upload-list-header{
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.form-label{
  display: flex;
  align-items: center;
}

.preview-wrapper{
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;

    .filename{
        padding: 4px;
    }
}

.select-controls{
  display: flex;
  align-items: center;
}

.delete-selected-button{
  margin-left: 10px;;
}
</style>